import "./index.scss";

import { Box, Stack } from "@mui/material";
import Button from "components/Button";
import Tabs from "components/Tabs";
import {
  DOCUMENT_BEING_PROCESSED,
  DOCUMENT_SENT_FAILED,
  DOCUMENT_SENT_SUCCESSFUL,
  TCA_REMS_ATD_THRESHOLD,
  TCA_REMS_SM_THRESHOLD,
  TCA_REMS_SS_THRESHOLD,
  WorkflowStates,
} from "constants/index";
import { logMessage, scrollToTop } from "helpers/utils";
import html2pdf from "html2pdf.js";
import { TMember } from "models/member";
import { TTCAInitialSteps } from "models/tca";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import ConfirmationBox from "../../../components/ConfirmationBox";
import { validateEmail } from "../../../modules/api/emailValidation";
import { emailContract } from "../../../modules/member/memberServices";
import PFI from "../components/PFI";
import PGP from "../components/PGP";
import REMS from "../components/REMS";
import SMS from "../components/SMS";
import TCAUserSummary from "../components/TCAUserSummary";

const InitialTCASteps = (props: {
  open: boolean;
  onClose: any;
  member: TMember;
  sendTCABackCallback: Function; //This is the function to be called with the tca object when it is ready/valid. The parent component will take care of actually saving the object in the database when its workflow sees fit
  takeMeBack: Function; //This is the function to take the user back when clicking on back on the first screen
}) => {
  const methods = useForm<TTCAInitialSteps>();
  const { getValues, watch, trigger, setValue } = methods;
  const { open, onClose, member, sendTCABackCallback, takeMeBack } = props;
  const [tabs, setTabs] = useState([]);
  const [tabValue, setTabValue] = useState("rems");
  const [currentPGPView, setCurrentPGPView] = useState<"form" | "graph">("form");
  const [currentPFIView, setCurrentPFIView] = useState<"form" | "graph">("form");
  const [submitButtonLabel, setSubmitButtonLabel] = useState("Next");
  const [saveButtonReminder, setSaveButtonReminder] = useState("");
  const [userIsSMSubscale, setUserIsSMSubscale] = useState<boolean>();
  const watchSM_REMS = watch(["remsQ2", "remsQ6", "remsQ11"]);
  const [userIsSSSubscale, setUserIsSSSubscale] = useState<boolean>();
  const watchSS_REMS = watch(["remsQ3", "remsQ8", "remsQ14"]);
  const [userIsATDSubscale, setUserIsATDSubscale] = useState<boolean>();
  const watchATD_REMS = watch(["remsQ5", "remsQ9", "remsQ13"]);
  const pgpRef = useRef(null);
  const pfiRef = useRef(null);

  const [showAddWorkoutConfirm, setShowAddWorkoutConfirm] = useState(false);

  const [showConfirmEmail, setShowConfirmEmail] = useState(false);
  const [contractEmail, setContractEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("Activelinxx - App Document");

  useEffect(() => {
    if (open) {
      setTabs([
        {
          label: "Resources for Exercise Maintenance Scale",
          state: WorkflowStates.active,
          value: "rems",
        },
        { label: "Self Management Skills", state: WorkflowStates.locked, value: "sms" },
        { label: "Personal Goal Profile", state: WorkflowStates.locked, value: "pgp" },
        { label: "Personal Feelings Inventory", state: WorkflowStates.locked, value: "pfi" },
      ]);
      setTabValue("rems"); //When opening, open the first tab all the time
    }
  }, [open]);

  useEffect(() => {
    if (tabValue === "pfi" && currentPFIView === "graph") {
      setSubmitButtonLabel("Save");
    } else {
      setSubmitButtonLabel("Next");
    }
  }, [tabValue, currentPFIView]);

  useEffect(() => {
    if (tabValue === "pfi" && currentPFIView === "graph") {
      setSaveButtonReminder("Remember to add a Workout Plan after saving this member");
    } else {
      setSaveButtonReminder("");
    }
  }, [tabValue, currentPFIView]);

  useEffect(() => {
    const sum = watchSM_REMS.map((m) => (isNaN(m) ? 0 : m)).reduce((acc, a) => acc + a, 0);
    setValue("remsSubscaleSM", sum);
    setUserIsSMSubscale(sum >= TCA_REMS_SM_THRESHOLD);
  }, [watchSM_REMS]);

  useEffect(() => {
    const sum = watchSS_REMS.map((m) => (isNaN(m) ? 0 : m)).reduce((acc, a) => acc + a, 0);
    setValue("remsSubscaleSS", sum);
    setUserIsSSSubscale(sum >= TCA_REMS_SS_THRESHOLD);
  }, [watchSS_REMS]);

  useEffect(() => {
    const sum = watchATD_REMS.map((m) => (isNaN(m) ? 0 : m)).reduce((acc, a) => acc + a, 0);
    setValue("remsSubscaleATD", sum);
    setUserIsATDSubscale(sum >= TCA_REMS_ATD_THRESHOLD);
  }, [watchATD_REMS]);

  //#region event handlers
  const handleCloseModal = () => {
    onClose();
  };

  const handleChangeTabByClickOnTab = (tabId) => {
    const destinationTab = tabs.find((f) => f.value === tabId);
    if (
      destinationTab.state === WorkflowStates.active ||
      destinationTab.state === WorkflowStates.complete
    ) {
      setTabValue(tabId);
    }
  };

  const handleNextClick = () => {
    scrollToTop(".personal-details-modal-container");
    if (tabValue === "rems") {
      trigger().then((validationResult) => {
        if (!validationResult) {
          return;
        }
        moveToNextTab();
        return;
      });
      return;
    }
    if (tabValue === "pgp") {
      if (currentPGPView === "form") {
        //Need to validate that we have at least 1 goal defined
        const pgpValidation = pgpRef.current.validate();
        if (pgpValidation > 0) {
          pgpRef.current.switchView("graph");
          setCurrentPGPView("graph");
        } else {
          if (pgpValidation === 0)
            toast("You need to specify at least 1 long term goal", { type: "error" });
        }
        return;
      } else {
        moveToNextTab();
        return;
      }
    }
    if (tabValue === "pfi") {
      if (currentPFIView === "form") {
        trigger().then((validationResult) => {
          if (!validationResult) {
            return;
          }
          pfiRef.current.switchView("graph");
          sendTCABackCallback(getValues()); // I had to add this after we incorporated next to move to the graph view. Otherwise, PFI values are lost
          setCurrentPFIView("graph");
          return;
        });
        return;
      } else {
        moveToNextTab();
        return;
      }
    }
    moveToNextTab(); //This is reached by tabs that don't need validation
  };

  const moveToNextTab = () => {
    let tempTabs = [...tabs];
    const currentTabIndex = tabs.findIndex((f) => f.value === tabValue);
    tempTabs[currentTabIndex].state = WorkflowStates.complete;

    if (currentTabIndex < tempTabs.length - 1) {
      tempTabs[currentTabIndex + 1].state = WorkflowStates.active;
      sendTCABackCallback(getValues());
      setTabValue(tabs[currentTabIndex + 1].value);
      setTabs([...tempTabs]);
    } else {
      handleFinalSave();
    }

    logMessage({
      member_id: member.member_id,
      message: `Got the next click. Current tab ${tabValue}`,
    });
  };

  const handleFinalSave = () => {
    setShowAddWorkoutConfirm(true);
  };

  const handleCloseWorkoutModal = (value: boolean | string) => {
    if (!value) {
      setShowAddWorkoutConfirm(false);
    } else if (value === "save_exit") {
      sendTCABackCallback(getValues(), true);
    } else if (value) {
      sendTCABackCallback(getValues(), true, true);
    }
  };

  const handleCloseEmailModal = (inputValue, subject) => {
    if (inputValue) {
      const newEmail = inputValue !== contractEmail ? inputValue : false;
      sendContractToBackend(false, newEmail, subject);
    }
    setShowConfirmEmail(false);
  };

  const handleBackClick = () => {
    if (tabValue === "pgp") {
      if (currentPGPView === "graph") {
        pgpRef.current.switchView("form");
        setCurrentPGPView("form");
        return;
      }
    }
    if (tabValue === "pfi") {
      if (currentPFIView === "graph") {
        pfiRef.current.switchView("form");
        setCurrentPFIView("form");
        return;
      }
    }

    const currentTabIndex = tabs.findIndex((f) => f.value === tabValue);

    if (currentTabIndex !== 0) {
      setTabValue(tabs[currentTabIndex - 1].value);
    } else {
      takeMeBack();
    }
  };
  //#endregion event handlers

  const sendContractToBackend = async (preview = true, newEmail = "", subject = "") => {
    const content = document.getElementsByClassName("elementToPrint")[0] as HTMLElement;
    setEmailSubject(`The Coach Approach Material - Initial Appointment`);

    const originalDisplay = content.style.display;
    const originalPadding = content.style.padding;

    content.style.display = "block";
    content.style.padding = "20mm";

    const options = {
      filename: `test.pdf`,
      html2canvas: { logging: true, scale: 1, useCORS: true },
      image: { quality: 0.98, type: "jpeg" },
      jsPDF: { format: "A4", orientation: "portrait" },
      margins: 1,
    };

    const blob = preview
      ? null
      : await html2pdf()
          .set(options)
          .from(content)
          .toPdf()
          .output("blob")
          .then((data: Blob) => {
            return data;
          });

    content.style.display = originalDisplay;
    content.style.padding = originalPadding;

    toast(DOCUMENT_BEING_PROCESSED, { type: "info" });

    if (member.email) {
      try {
        if (preview) {
          setContractEmail(member.email);
          setShowConfirmEmail(true);
        } else {
          const email = newEmail ? newEmail : member.email;
          const isValid = await validateEmail(email.toLowerCase());
          if (!isValid) {
            return setShowConfirmEmail(true);
          }

          await emailContract(0, blob, preview, email, subject);
          toast(DOCUMENT_SENT_SUCCESSFUL, { type: "success" });
        }
      } catch (e) {
        console.log("e", e);
        toast(DOCUMENT_SENT_FAILED, { type: "error" });
      }
    } else if (member.id) {
      try {
        const email = newEmail ? newEmail : contractEmail;

        if (!preview) {
          const isValid = await validateEmail(email.toLowerCase());
          if (!isValid) {
            return setShowConfirmEmail(true);
          }
        }
        const response = await emailContract(member.id, blob, preview, email, subject);
        if (preview) {
          setContractEmail(response?.payload?.strEmailAddress);
          setShowConfirmEmail(true);
        } else {
          toast(DOCUMENT_SENT_SUCCESSFUL, { type: "success" });
        }
      } catch (e) {
        console.log("e", e);
        toast(DOCUMENT_SENT_FAILED, { type: "error" });
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <Box className="initialTCASteps">
        <Tabs
          style={{ marginBottom: 30 }}
          value={tabValue}
          onChange={handleChangeTabByClickOnTab}
          tabs={tabs}
        />
        <TCAUserSummary
          member={member}
          appointment={"Initial Appointment"}
          isSMSubscale={userIsSMSubscale}
          isSSSubscale={userIsSSSubscale}
          isATDSubscale={userIsATDSubscale}
        />
        <form>
          <Stack height={"100%"}>
            <REMS currentTab={tabValue} />
            <SMS
              currentTab={tabValue}
              appointmentNumber={0}
              sendContractToBackend={sendContractToBackend}
            />
            <PGP
              currentTab={tabValue}
              goals={watch("pgp")}
              appointmentNumber={0}
              ref={pgpRef}
              shareSubView={(v) => setCurrentPGPView(v ? `graph` : `form`)}
            />
            <PFI
              currentTab={tabValue}
              ref={pfiRef}
              shareSubView={(v) => setCurrentPFIView(v ? `graph` : `form`)}
            />
          </Stack>
          <Stack direction="column" justifyContent="flex-end">
            <Stack direction="row" alignItems="center" justifyContent="flex-end" marginTop="30px">
              <Stack direction="row" alignItems="center" spacing={3} flexGrow={1}>
                <Box flexGrow={1}>
                  <Button
                    label="Back"
                    type="button"
                    className="text-button"
                    onClick={() => handleBackClick()}
                  />
                </Box>
                <Button
                  label="Cancel"
                  type="button"
                  className="text-button"
                  onClick={handleCloseModal}
                />
                <Button
                  label={submitButtonLabel}
                  type="button"
                  className="confirm-button"
                  onClick={handleNextClick}
                />
              </Stack>
            </Stack>
            <h3 className="in-line-reminder">{saveButtonReminder}</h3>
          </Stack>
        </form>
      </Box>
      <ConfirmationBox
        mainTitle={"Saving Member"}
        open={showAddWorkoutConfirm}
        onClose={handleCloseWorkoutModal}
        title={"What do you want to do?"}
        message={
          <>Do you wish to save and add a new Workout Plan to this member, or only save and exit?</>
        }
        type={"confirmation"}
        noButtonText={"Save and Exit"}
        yesButtonText={"Add New Plan"}
        specialNoValue={"save_exit"}
      />
      <ConfirmationBox
        open={showConfirmEmail}
        onClose={handleCloseEmailModal}
        mainTitle={"Send Document"}
        title={"Sending Coach Approach Document"}
        message={<>Please verify that the member's email address is valid</>}
        type={"confirmation"}
        initialInputValue={contractEmail}
        yesButtonText="Send"
        noButtonText="Cancel"
        initialInputValueAlt={emailSubject}
      />
    </FormProvider>
  );
};

export default InitialTCASteps;
